import React, {useEffect} from 'react'
import {RemoveScroll} from 'react-remove-scroll'
import {Box, BoxProps, useMultiStyleConfig} from '@chakra-ui/react'
import {useHeaderState} from '_context'
import {useViewport, useCustomApi, useAnalytics, useOpenModal} from '_customHooks'
import Topbar from './partials/topbar'
import SearchSection from './partials/search-section'
import Sidebar from './partials/sidebar'
import {Banner} from './partials/banner'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {getCookie, setCountrySelectionCookie, COOKIE_REGISTRY} from '_utils'
import {useUsid, useShopperContext} from '@salesforce/commerce-sdk-react'
import {isServer} from '@salesforce/retail-react-app/app/utils/utils'
import AccessibilityBanner from './partials/accessibilityBanner'
import {SkipNavLink} from '@chakra-ui/skip-nav'

/**
 * @name Header
 *
 * @component
 *
 * @description
 * Manages the render of the topbar, menu, banner, sidebar and search section.
 *
 * @remarks
 * - The locale modal is handled here because it's related to a global context.
 * - Usage of `useHeaderState` here could affect performance; if you add some new states to the context remember
 * to memoize the components that will be using them to avoid re-renders related issues.
 * - SkinNavLink should be after the usableNet Banner
 * - The `HeaderProvider` is mounted ONCE in `_app`, wrapping BOTH this Header and the page
 *   `{children}`. Do NOT wrap the Header in its own `HeaderProvider`: a nested provider creates a
 *   second, isolated store, so header state set from a page (e.g. `setTransparentMode` on Editorial)
 *   or read by a page (e.g. `isSidebarOpen` to hide the PDP sticky BUY CTA) never crosses between the
 *   two. A single provider in `_app` keeps the state shared app-wide.
 *
 * @returns {React.FC} - Rendered `Header` component.
 */
const Header: React.FC<{shopperContext?: {clientIp?: string}}> = ({shopperContext}) => {
    const {headerRef, isSearchSectionEnabled, isSidebarOpen, isTransparentMode, isAtTop, showBanner, isHomepage} = useHeaderState() // TODO check here isTransparentMode and isHomepage
    const {openSwitchLocaleModal} = useOpenModal()
    const {isDesktop} = useViewport()
    const {site, locale} = useMultiSite()
    const {usid} = useUsid()
    const {analyticsEventCheckLocale} = useAnalytics()
    const {useVerifyGeolocation} = useCustomApi()

    const {data: fetchedShopperContext} = useShopperContext({parameters: {usid, siteId: site.id}}, {enabled: !isServer && Boolean(usid) && !shopperContext})
    const finalShopperContext = shopperContext || fetchedShopperContext

    const {data: geolocation} = useVerifyGeolocation(locale.id, usid, finalShopperContext?.clientIp)

    const styles = useMultiStyleConfig('Header', {isTransparentMode}) as Record<string, BoxProps>
    const hasCountrySelection = Boolean(getCookie(COOKIE_REGISTRY.BC_FCOUNTRY.name))
    const hasSelectedCountry = Boolean(getCookie(COOKIE_REGISTRY.BC_COUNTRYSELECTION.name))

    const triggerSwitchLocaleModal = () => {
        openSwitchLocaleModal({
            labels: geolocation?.assets || [],
            locale: geolocation?.locale || locale,
            onClose: () => {
                const currentLocale = geolocation?.locale?.current.value

                setCountrySelectionCookie(currentLocale.split('_')[1])
                analyticsEventCheckLocale({
                    action: 'close',
                    label: currentLocale.split('_')[1],
                })
            },
        })
    }

    useEffect(() => {
        if (geolocation?.suggestCountryChange) {
            if (hasCountrySelection || hasSelectedCountry) {
                const cookieCountryCode = getCookie(COOKIE_REGISTRY.BC_FCOUNTRY.name)
                const cookieSelectedCountry = getCookie(COOKIE_REGISTRY.BC_COUNTRYSELECTION.name)
                const activeCountryCode = locale.id.split('-')[1]

                if (cookieSelectedCountry === activeCountryCode || cookieCountryCode === activeCountryCode) return

                triggerSwitchLocaleModal()
            } else {
                triggerSwitchLocaleModal()
            }
        }
    }, [geolocation])

    return (
        <RemoveScroll enabled={isSidebarOpen && !isDesktop} noIsolation={true}>
            <Box ref={headerRef} data-testid="header" {...styles.header} as="header">
                <AccessibilityBanner isAtTop={isAtTop} />

                <SkipNavLink
                    zIndex="skipLink"
                    sx={{
                        // The skip link must NEVER appear on mouse/programmatic focus (it was popping up
                        // during plain desktop navigation) and must be exposed ONLY when the UsableNet
                        // assistive mode is active (html.un-assistive) AND the focus comes from the
                        // keyboard. In every other case it stays visually hidden.
                        //
                        // Chakra's SkipNavLink opens itself on plain `:focus` (clip/width/height:auto);
                        // we neutralize that here so a bare `:focus` (mouse/programmatic) keeps it
                        // clipped, then re-open it only for the "assistive + keyboard" combination.
                        //
                        // Keyboard focus is detected via Chakra's focus-visible polyfill (it stamps
                        // `data-focus-visible-added` on keyboard focus, see `[data-js-focus-visible]`
                        // rules in globalStyles) with the native `:focus-visible` as a fallback for
                        // browsers where the polyfill is inactive.
                        '&:focus': {
                            clip: 'rect(0 0 0 0)',
                            width: '1px',
                            height: '1px',
                        },
                        '.un-assistive &:focus-visible, .un-assistive &[data-focus-visible-added]': {
                            clip: 'auto',
                            width: 'auto',
                            height: 'auto',
                        },
                    }}
                >
                    Skip to Content
                </SkipNavLink>

                <Topbar isAtTop={isAtTop} isTransparentMode={isTransparentMode} isSearchSectionEnabled={isSearchSectionEnabled} isHomepage={isHomepage} />

                {showBanner && <Banner />}

                {isSidebarOpen && !isDesktop && <Sidebar />}

                {isSearchSectionEnabled && <SearchSection isAtTop={isAtTop} />}
            </Box>
        </RemoveScroll>
    )
}

export {Header}
